import { NgIf, PercentPipe } from '@angular/common';
import { Component, Inject, OnDestroy, ViewChild } from '@angular/core';
import { FlexModule } from '@angular/flex-layout';
import { MatAnchor, MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { MomentModule } from 'ngx-moment';
import { Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Docket } from '../../../models/index';
import { base64ToBlob } from '../../helpers/base64ToBlob';
import { HireItemListStoreService } from '../../hire-items-list/hire-item-list-v2/hire-item-list-store.service';
import { HireItemListV2Component } from '../../hire-items-list/hire-item-list-v2/hire-item-list-v2.component';
import { DocketService, HireItemService } from '../../index';
import { CustomerAddressPipe } from '../../pipes/customer-address.pipe';
import { CustomerContactDetailsPipe } from '../../pipes/customer-contact-details.pipe';
import { CustomerNamePipe } from '../../pipes/customer-name.pipe';
import { RetailItemListComponent } from '../../retail-item-list/retail-item-list.component';
import {
  RetailItemStoreService,
  SALE_ITEM_SERVICE
} from '../../retail-item-list/retail-item.store.service';
import { ConfigService } from '../../services/config.service';
import { DocketRetailItemSaleService } from '../../services/docket-retail-item-sale.service';

@Component({
  selector: 'preview-docket-dialog',
  templateUrl: './preview-docket-dialog.component.html',
  styleUrls: ['./preview-docket-dialog.component.css'],
  providers: [
    DocketService,
    CustomerNamePipe,
    RetailItemStoreService,
    { provide: SALE_ITEM_SERVICE, useClass: DocketRetailItemSaleService },
    HireItemListStoreService,
    HireItemService
  ],
  standalone: true,
  imports: [
    NgIf,
    MatDialogTitle,
    MatDialogContent,
    FlexModule,
    MatTooltip,
    RouterLink,
    MatButton,
    MatIcon,
    RetailItemListComponent,
    MatDialogActions,
    MatAnchor,
    PercentPipe,
    CustomerNamePipe,
    CustomerContactDetailsPipe,
    CustomerAddressPipe,
    MomentModule,
    HireItemListV2Component
  ]
})
export class PreviewDocketDialogComponent implements OnDestroy {
  docket: Docket;
  statusHeading = '';
  private ngUnsubscribe = new Subject<void>();
  currentLocation = Location;
  @ViewChild('printFrame', { static: true }) printFrame;
  dateFormat = ConfigService.dates.dateFormat;
  dateTimeFormat = ConfigService.dates.longDateTimeFormat;
  currency = 'GBP';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    private mdDialogRef: MatDialogRef<PreviewDocketDialogComponent>,
    private router: Router,
    private docketService: DocketService,
    private customerNamePipe: CustomerNamePipe,
    private configService: ConfigService
  ) {
    const docketId = data.docketId;
    this.docketService
      .getDocket(docketId, ['customer'])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((docket) => {
        this.docket = docket;
        this.setStatusHeading();
      });

    this.configService.userConfig.subscribe((config) => {
      if ('currency' in config) {
        this.currency = config['currency'];
      }
    });

    this.router.events.subscribe((event) => {
      this.mdDialogRef.close();
    });
  }

  close() {
    this.mdDialogRef.close();
  }

  setStatusHeading() {
    if (this.docket.account_reference) {
      this.statusHeading = 'Hire Invoice';
    } else {
      this.statusHeading = 'Hire Contract';
    }
  }

  print(download = false) {
    this.docketService
      .print2(this.docket.id, this.currentLocation.name)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((response) => {
        let src = URL.createObjectURL(
          base64ToBlob(response, 'application/pdf')
        );
        if (download) {
          const link = document.createElement('a');
          link.href = src;
          const ref = this.docket.account_reference
            ? this.docket.account_reference
            : this.docket.id;
          link.download = `${ref} (${this.customerNamePipe.transform(
            this.docket.customer
          )})`;
          link.click();
        } else {
          this.printFrame.nativeElement.src = src;
          timer(500)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
              this.printFrame.nativeElement.contentWindow.print();
            });
        }
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
